import { useStore } from "ra-core";
import { useCallback, useEffect, useMemo } from "react";
import { getSupabaseClient } from "../providers/supabase/supabase";

export type CustomFieldType =
  | "text"
  | "textarea"
  | "select"
  | "multiselect"
  | "autocomplete"
  | "number"
  | "currency"
  | "user"
  | "time"
  | "date"
  | "address";

export const CUSTOM_FIELD_TYPE_LABELS: Record<CustomFieldType, string> = {
  text: "Texto (linha única)",
  textarea: "Texto longo",
  select: "Opção única",
  multiselect: "Múltipla escolha",
  autocomplete: "Autocompletar",
  number: "Número",
  currency: "Monetário (R$)",
  user: "Usuário",
  time: "Hora",
  date: "Data",
  address: "Endereço",
};

export interface DealCustomField {
  id: string;
  name: string;
  type: CustomFieldType;
  options?: string[];
  showOnCreate: boolean;
  requiredRule?: {
    requiredOnCreate?: boolean;
    requiredStages?: string[];
    requiredPipelines?: string[];
  };
  order: number;
}

const DEFAULT_CUSTOM_FIELDS: DealCustomField[] = [
  {
    id: "cf_segmento_detalhado",
    name: "Segmento de Atuação",
    type: "text",
    showOnCreate: true,
    order: 0,
  },
  {
    id: "cf_complexidade_projeto",
    name: "Complexidade do Projeto",
    type: "select",
    options: ["Baixa", "Média", "Alta", "Especial"],
    showOnCreate: false,
    order: 1,
  },
];

export const DEFAULT_CRM_ID = "fe386f56-b27f-446f-86ec-39a03b64add1"; // Grupo Dotus

export const getActiveCrmId = (): string => {
  if (typeof window === "undefined" || !window.localStorage) {
    return DEFAULT_CRM_ID;
  }
  const raw = window.localStorage.getItem("dotus_active_crm_id");
  if (!raw || raw === "00000000-0000-0000-0000-000000000001") {
    return DEFAULT_CRM_ID;
  }
  return raw;
};

export const getDealCustomFieldsStoreKey = (crmId: string = getActiveCrmId()) =>
  `crm.${crmId}.deal_custom_fields`;

async function syncDealFieldsToSupabase(
  fields: DealCustomField[],
  targetCrmId: string = getActiveCrmId(),
) {
  try {
    const client = getSupabaseClient();
    const activeCrmId = targetCrmId || getActiveCrmId();

    // 1. Sincroniza nas configurações do CRM específico em public.crms
    if (activeCrmId) {
      try {
        const { data: crmData } = await client
          .from("crms")
          .select("settings")
          .eq("id", activeCrmId)
          .maybeSingle();
        const settings = crmData?.settings || {};
        await client
          .from("crms")
          .update({ settings: { ...settings, dealCustomFields: fields } })
          .eq("id", activeCrmId);
      } catch (err) {
        console.warn(
          "Could not sync deal custom fields to crms settings:",
          err,
        );
      }
    }

    // 2. Sincroniza também na tabela formal custom_field_definitions estritamente escopada pelo crm_id
    for (const f of fields) {
      if (!f.id || !f.name) continue;
      await client.from("custom_field_definitions").upsert(
        {
          crm_id: activeCrmId,
          entity_type: "deal",
          key: f.id,
          label: f.name,
          field_type: f.type || "text",
          options: f.options || [],
          order_nr: f.order ?? 0,
          required: !!f.requiredRule?.requiredOnCreate,
        },
        { onConflict: "crm_id,entity_type,key" },
      );
    }
  } catch (err) {
    console.warn("Could not sync deal custom fields to Supabase:", err);
  }
}

export const useDealCustomFields = () => {
  const activeCrmId = getActiveCrmId();
  const storeKey = useMemo(
    () => getDealCustomFieldsStoreKey(activeCrmId),
    [activeCrmId],
  );

  const [fields, setFields] = useStore<DealCustomField[]>(
    storeKey,
    DEFAULT_CUSTOM_FIELDS,
  );

  // Sync from Supabase on mount or whenever activeCrmId changes
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const client = getSupabaseClient();
        // 1. Tenta carregar primeiro da tabela custom_field_definitions ESTRITAMENTE do crm_id ativo
        const { data: dbDefinitions } = await client
          .from("custom_field_definitions")
          .select("*")
          .eq("crm_id", activeCrmId)
          .eq("entity_type", "deal")
          .order("order_nr", { ascending: true });

        if (isMounted && dbDefinitions && dbDefinitions.length > 0) {
          const mapped: DealCustomField[] = dbDefinitions.map((d: any) => ({
            id: d.key,
            name: d.label,
            type: d.field_type as CustomFieldType,
            options: Array.isArray(d.options) ? d.options : [],
            order: d.order_nr ?? 0,
            showOnCreate: true,
          }));
          setFields(mapped);
          return;
        }

        // 2. Fallback: carrega das settings do CRM ativo
        const { data: crmData } = await client
          .from("crms")
          .select("settings")
          .eq("id", activeCrmId)
          .maybeSingle();

        const saved = crmData?.settings?.dealCustomFields;
        if (isMounted && saved && Array.isArray(saved) && saved.length > 0) {
          setFields(saved);
          return;
        }

        // 3. Se não houver campos configurados para este CRM, usa os campos padrão apenas para o CRM principal
        if (isMounted) {
          setFields(
            activeCrmId === DEFAULT_CRM_ID ? DEFAULT_CUSTOM_FIELDS : [],
          );
        }
      } catch (err) {
        console.warn("Erro ao carregar detalhes do negócio para o CRM:", err);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [activeCrmId, setFields]);

  const sortedFields = [...(fields || [])].sort((a, b) => a.order - b.order);

  const addField = useCallback(
    (fieldData: Omit<DealCustomField, "id" | "order">) => {
      let slug =
        "cf_" +
        fieldData.name
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]/g, "_")
          .replace(/_+/g, "_")
          .substring(0, 30);
      if (slug.endsWith("_")) slug = slug.slice(0, -1);

      const newField: DealCustomField = {
        ...fieldData,
        id: slug,
        order: fields ? fields.length : 0,
      };

      const updated = [...(fields || []), newField];
      setFields(updated);
      syncDealFieldsToSupabase(updated, activeCrmId);
      return newField;
    },
    [fields, setFields, activeCrmId],
  );

  const updateField = useCallback(
    (id: string, updatedData: Partial<Omit<DealCustomField, "id">>) => {
      const updated = (fields || []).map((f) =>
        f.id === id ? { ...f, ...updatedData } : f,
      );
      setFields(updated);
      syncDealFieldsToSupabase(updated, activeCrmId);
    },
    [fields, setFields, activeCrmId],
  );

  const deleteField = useCallback(
    async (id: string) => {
      const updated = (fields || []).filter((f) => f.id !== id);
      setFields(updated);
      syncDealFieldsToSupabase(updated, activeCrmId);
      try {
        await getSupabaseClient()
          .from("custom_field_definitions")
          .delete()
          .eq("crm_id", activeCrmId)
          .eq("entity_type", "deal")
          .eq("key", id);
      } catch (err) {
        console.warn("Could not delete from custom_field_definitions:", err);
      }
    },
    [fields, setFields, activeCrmId],
  );

  const reorderFields = useCallback(
    (reordered: DealCustomField[]) => {
      const withUpdatedOrders = reordered.map((f, idx) => ({
        ...f,
        order: idx,
      }));
      setFields(withUpdatedOrders);
      syncDealFieldsToSupabase(withUpdatedOrders, activeCrmId);
    },
    [setFields, activeCrmId],
  );

  return {
    fields: sortedFields,
    addField,
    updateField,
    deleteField,
    reorderFields,
  };
};
